import React, { useMemo } from 'react';
import { getImageUrl } from '../utils/apiConfig';
import HighlightCard from './HighlightCard';

/**
 * QuotasHero - Trends & Highlights for Scores/Quotas page
 *
 * Shows player spotlights celebrating achievements:
 * - Best Average (highest average score - consistency king)
 * - Best Round (highest single score - hot hand)
 * - Most Improved (biggest quota increase - grinding paying off)
 * - Quota Buster (highest % of rounds beating quota)
 */
const QuotasHero = ({
  players = [],
  getPlayerImageUrl,
}) => {
  // Calculate highlights and quick stats from player data using single-pass reduce
  // Note: Stableford scoring - HIGHER scores are better
  const { highlights, quickStats } = useMemo(() => {
    if (!players || players.length === 0) {
      return {
        highlights: { bestAverage: null, bestRound: null, mostImproved: null, quotaBuster: null },
        quickStats: { totalPlayers: 0, avgQuota: 0, avgScore: 0 },
      };
    }

    // Single pass to find all max values and compute stats
    const result = players.reduce(
      (acc, player) => {
        const averageScore = player.average_score || 0;
        const bestScore = player.best_score || 0;
        const quotaChange = player.quota_change || 0;
        const beatQuotaPct = player.beat_quota_pct || 0;
        const eventsPlayed = player.events_played || 0;
        const currentQuota = player.current_quota || 0;

        // Best Average - HIGHEST average score (Stableford: higher = better)
        if (averageScore > 0 && averageScore > (acc.maxAverage || 0)) {
          acc.maxAverage = averageScore;
          acc.bestAverage = player;
        }

        // Best Round - HIGHEST single score (Stableford: higher = better)
        if (bestScore > 0 && bestScore > (acc.maxBestScore || 0)) {
          acc.maxBestScore = bestScore;
          acc.bestRound = player;
        }

        // Most Improved - biggest quota INCREASE (positive quota_change, min 5 events)
        if (quotaChange > 0 && eventsPlayed >= 5 && quotaChange > (acc.maxImprovement || 0)) {
          acc.maxImprovement = quotaChange;
          acc.mostImproved = player;
        }

        // Quota Buster - highest beat quota percentage (min 5 events)
        if (beatQuotaPct > 0 && eventsPlayed >= 5 && beatQuotaPct > (acc.maxBeatPct || 0)) {
          acc.maxBeatPct = beatQuotaPct;
          acc.quotaBuster = player;
        }

        // Quick stats accumulators
        acc.totalQuota += currentQuota;
        acc.totalAvgScore += averageScore;
        acc.playerCount++;

        return acc;
      },
      {
        bestAverage: null,
        bestRound: null,
        mostImproved: null,
        quotaBuster: null,
        maxAverage: 0,
        maxBestScore: 0,
        maxImprovement: 0,
        maxBeatPct: 0,
        totalQuota: 0,
        totalAvgScore: 0,
        playerCount: 0,
      }
    );

    return {
      highlights: {
        bestAverage: result.bestAverage,
        bestRound: result.bestRound,
        mostImproved: result.mostImproved,
        quotaBuster: result.quotaBuster,
      },
      quickStats: {
        totalPlayers: result.playerCount,
        avgQuota: result.playerCount > 0 ? (result.totalQuota / result.playerCount).toFixed(1) : 0,
        avgScore: result.playerCount > 0 ? (result.totalAvgScore / result.playerCount).toFixed(1) : 0,
      },
    };
  }, [players]);

  const getImageSrc = (player) => {
    if (!player) return '/default-avatar.png';
    if (getPlayerImageUrl) return getPlayerImageUrl(player);
    if (player.image_path) return getImageUrl(player.image_path);
    return '/default-avatar.png';
  };

  const { totalPlayers, avgQuota, avgScore } = quickStats;

  return (
    <div className="mb-6">
      {/* Quick Stats Row */}
      <div className="flex flex-wrap items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
            {totalPlayers}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">Players</span>
        </div>
        <div className="w-px h-6 bg-gray-300 dark:bg-navy-600" />
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-forest-600 dark:text-forest-400 tabular-nums">
            {avgQuota}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">Avg Quota</span>
        </div>
        <div className="w-px h-6 bg-gray-300 dark:bg-navy-600 hidden sm:block" />
        <div className="hidden sm:flex items-center gap-2">
          <span className="text-2xl font-bold text-blue-600 dark:text-blue-400 tabular-nums">
            {avgScore}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">Avg Score</span>
        </div>
      </div>

      {/* Highlights Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <HighlightCard
          title="The Blueprint"
          subtitle="Best Average Score"
          backgroundImage="/assets/best-average.png"
          accentColor="bg-gradient-to-r from-emerald-400 to-emerald-600"
          player={highlights.bestAverage}
          playerImageSrc={getImageSrc(highlights.bestAverage)}
          stat={highlights.bestAverage?.average_score?.toFixed(1) || '—'}
          statLabel="Avg"
          statColorClass="text-emerald-600 dark:text-emerald-400"
        />

        <HighlightCard
          title="The Go-Low"
          subtitle="Best Single Round"
          backgroundImage="/assets/best-round.png"
          accentColor="bg-gradient-to-r from-blue-400 to-blue-600"
          player={highlights.bestRound}
          playerImageSrc={getImageSrc(highlights.bestRound)}
          stat={highlights.bestRound?.best_score || '—'}
          statLabel="Points"
          statColorClass="text-blue-600 dark:text-blue-400"
        />

        <HighlightCard
          title="New Game Hustla"
          subtitle="Most Improved Quota"
          backgroundImage="/assets/most-improved.png"
          accentColor="bg-gradient-to-r from-forest-400 to-forest-600"
          player={highlights.mostImproved}
          playerImageSrc={getImageSrc(highlights.mostImproved)}
          stat={highlights.mostImproved ? `+${highlights.mostImproved.quota_change}` : '—'}
          statLabel="Quota"
          statColorClass="text-forest-600 dark:text-forest-400"
        />

        <HighlightCard
          title="Quota Buster"
          subtitle="Most Times Beating Quota"
          backgroundImage="/assets/quota-buster.png"
          accentColor="bg-gradient-to-r from-orange-400 to-red-500"
          player={highlights.quotaBuster}
          playerImageSrc={getImageSrc(highlights.quotaBuster)}
          stat={highlights.quotaBuster ? `${highlights.quotaBuster.beat_quota_pct}%` : '—'}
          statLabel="Beat %"
          statColorClass="text-orange-600 dark:text-orange-400"
        />
      </div>
    </div>
  );
};

export default QuotasHero;
